import { useEffect, useState } from "react";
import { Link } from "wouter";
import { api, type Club } from "../lib/api";
import { Shell } from "../components/Shell";

export default function ClubsList() {
  const [clubs, setClubs] = useState<Club[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.listClubs().then(setClubs).catch((e) => setErr(e.message));
  }, []);

  const bookable = (clubs ?? []).filter(c => c.is_bookable);
  const comingSoon = (clubs ?? []).filter(c => !c.is_bookable);

  return (
    <Shell>
      <section className="mx-auto max-w-6xl px-6 pt-16 pb-12">
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-balance">
          Find your tee time
        </h1>
        <p className="mt-4 text-lg text-neutral-400 max-w-2xl">
          Premium clubs across Southern Africa. Book in seconds, pay at the club.
        </p>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-20">
        {err && <div className="rounded-lg border border-red-900/50 bg-red-950/30 text-red-200 p-4 text-sm">{err}</div>}

        {!clubs && !err && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-72 rounded-2xl bg-neutral-900/50 border border-neutral-900 animate-pulse" />
            ))}
          </div>
        )}

        {bookable.length > 0 && (
          <>
            <h2 className="text-sm uppercase tracking-wider text-neutral-500 mb-4">Bookable now</h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 mb-12">
              {bookable.map(c => <ClubCard key={c.id} club={c} bookable />)}
            </div>
          </>
        )}

        {comingSoon.length > 0 && (
          <>
            <h2 className="text-sm uppercase tracking-wider text-neutral-500 mb-4">Coming soon</h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {comingSoon.map(c => <ClubCard key={c.id} club={c} bookable={false} />)}
            </div>
          </>
        )}
      </section>
    </Shell>
  );
}

function ClubCard({ club, bookable }: { club: Club; bookable: boolean }) {
  const inner = (
    <div className={`group rounded-2xl overflow-hidden border border-neutral-900 bg-neutral-900/30 hover:border-neutral-800 transition-colors ${bookable ? "" : "opacity-60"}`}>
      <div className="aspect-[16/10] bg-neutral-900 overflow-hidden">
        {club.photo_url ? (
          <img
            src={club.photo_url}
            alt={club.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-neutral-800 to-neutral-900" />
        )}
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-medium text-neutral-0">{club.name}</div>
            <div className="text-sm text-neutral-500 mt-1">
              {[club.city, club.province].filter(Boolean).join(", ") || "South Africa"}
            </div>
          </div>
          {bookable ? (
            <span className="shrink-0 text-xs font-medium rounded-full bg-accent-500/15 text-accent-300 px-2.5 py-1">
              Available
            </span>
          ) : (
            <span className="shrink-0 text-xs font-medium rounded-full bg-neutral-800 text-neutral-400 px-2.5 py-1">
              Coming soon
            </span>
          )}
        </div>
      </div>
    </div>
  );
  if (!bookable) return inner;
  return <Link href={`/clubs/${club.slug}`}>{inner}</Link>;
}
